import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend 
} from 'recharts';
import * as Icons from 'react-icons/fa';
import { 
  FaChartPie, FaFileDownload, FaCalendarAlt, FaRupeeSign, 
  FaPercentage, FaEye, FaCheckDouble 
} from 'react-icons/fa';

// --- PRO THEME ---
const THEME = {
  primary: '#0f172a',
  accent: '#6366f1',    // Indigo
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  bg: '#f8fafc',
  textMain: '#1e293b',
  textSub: '#64748b',
  border: '#e2e8f0'
};

// --- CONFIG ---
const COST_PER_MSG = 0.80; // INR
const EST_LEAD_VALUE = 1500; // INR (Estimated value of a converted lead)

const KPICard = ({ title, value, icon, color, trend, prefix = "" }) => (
  <div style={{ background: 'white', padding: '24px', borderRadius: '16px', border: `1px solid ${THEME.border}`, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
      <div>
        <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: THEME.textSub, letterSpacing: '0.5px' }}>{title}</p>
        <h3 style={{ margin: '5px 0 0 0', fontSize: '26px', fontWeight: '800', color: THEME.textMain }}>{prefix}{value}</h3>
      </div>
      <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${color}15`, color: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
        {icon}
      </div>
    </div>
    {trend && (
      <div style={{ fontSize: '12px', fontWeight: '600', color: trend >= 0 ? THEME.success : THEME.danger, display: 'flex', alignItems: 'center', gap: '4px' }}>
        {trend >= 0 ? <Icons.FaArrowUp size={10} /> : <Icons.FaArrowDown size={10} />}
        {Math.abs(trend)}% vs last period
      </div>
    )}
  </div>
);

function Analytics() {
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Data States
  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  
  // Filter States
  const [dateRange, setDateRange] = useState('30days'); 
  
  // Metrics
  const [metrics, setMetrics] = useState({
    totalSpend: 0, totalSent: 0, totalDelivered: 0, totalRead: 0,
    deliveryRate: 0, readRate: 0, roi: 0
  });
  
  // Charts
  const [trendData, setTrendData] = useState([]);
  const [funnelData, setFunnelData] = useState([]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 1. FETCH DATA
  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((u) => {
      setUser(u);
      if (u) {
        const q = query(collection(db, "businesses", u.uid, "broadcasts"), orderBy("createdAt", "desc"));
        onSnapshot(q, (snapshot) => {
          const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setRawData(data);
        });
      }
    });
    return () => unsubscribeAuth();
  }, []);

  // 2. PROCESS DATA (Filter & Calculate)
  useEffect(() => {
    if (!rawData.length) return;

    // Filter Logic
    const now = new Date();
    const cutoff = new Date();
    if (dateRange === 'today') cutoff.setHours(0,0,0,0);
    if (dateRange === '7days') cutoff.setDate(now.getDate() - 7);
    if (dateRange === '30days') cutoff.setDate(now.getDate() - 30);
    if (dateRange === 'all') cutoff.setFullYear(2000); // Way back

    const filtered = rawData.filter(item => {
      if (!item.createdAt) return false;
      return item.createdAt.toDate() >= cutoff;
    });

    setFilteredData(filtered);

    // Calc Totals
    let sent = 0, delivered = 0, read = 0;
    const chartMap = {};

    filtered.forEach(camp => {
      sent += (camp.sent || 0);
      delivered += (camp.delivered || 0); // Assuming you track this, else fallback to sent
      read += (camp.read || 0);

      // Trend Data Grouping
      const dateKey = camp.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (!chartMap[dateKey]) chartMap[dateKey] = { date: dateKey, Sent: 0, Read: 0, Spend: 0 };
      chartMap[dateKey].Sent += (camp.sent || 0);
      chartMap[dateKey].Read += (camp.read || 0);
      chartMap[dateKey].Spend += ((camp.sent || 0) * COST_PER_MSG);
    });

    const totalSpend = sent * COST_PER_MSG;
    const estRevenue = read * (EST_LEAD_VALUE * 0.05); // Assume 5% of readers convert
    const roi = totalSpend === 0 ? 0 : ((estRevenue - totalSpend) / totalSpend) * 100;

    setMetrics({
      totalSpend, totalSent: sent, totalDelivered: delivered || sent, totalRead: read,
      deliveryRate: sent === 0 ? 0 : Math.round((delivered || sent) / sent * 100),
      readRate: sent === 0 ? 0 : Math.round(read / sent * 100),
      roi: Math.round(roi)
    });

    // Chart Data
    setTrendData(Object.values(chartMap).reverse());
    setFunnelData([
      { name: 'Sent', value: sent },
      { name: 'Delivered', value: delivered || sent },
      { name: 'Read', value: read }
    ]);

  }, [rawData, dateRange]);

  // --- EXPORT CSV FUNCTION ---
  const handleExport = () => {
    const headers = ["Campaign Name,Date,Sent,Read,Spend (INR),Status"];
    const rows = filteredData.map(c => 
      `${c.name},${c.createdAt.toDate().toLocaleDateString()},${c.sent},${c.read},${(c.sent * COST_PER_MSG).toFixed(2)},${c.status}`
    );
    const csvContent = "data:text/csv;charset=utf-8," + [headers, ...rows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `marketing_report_${dateRange}.csv`);
    document.body.appendChild(link);
    link.click();
  };

  if (!user) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading Analytics...</div>;

  return (
    <div style={{ fontFamily: '"Inter", sans-serif', background: THEME.bg, minHeight: '100vh', padding: isMobile ? '15px' : '30px' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: '30px', gap: '15px' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: '800', color: THEME.textMain, margin: 0 }}>Analytics & ROI</h1>
          <p style={{ margin: '5px 0 0 0', color: THEME.textSub, fontSize: '14px' }}>Deep dive into your marketing performance.</p>
        </div>
        
        <div style={{ display: 'flex', gap: '10px' }}>
          {/* Date Filter */}
          <div style={{ background: 'white', padding: '5px', borderRadius: '12px', border: `1px solid ${THEME.border}`, display: 'flex', alignItems: 'center' }}>
            <FaCalendarAlt style={{ margin: '0 10px', color: THEME.textSub }} />
            <select 
              value={dateRange} 
              onChange={(e) => setDateRange(e.target.value)} 
              style={{ border: 'none', background: 'transparent', outline: 'none', padding: '8px', fontWeight: '600', color: THEME.textMain, cursor: 'pointer' }}
            >
              <option value="today">Today</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="all">All Time</option>
            </select>
          </div>

          <button onClick={handleExport} style={{ background: 'white', border: `1px solid ${THEME.border}`, color: THEME.textMain, padding: '10px 20px', borderRadius: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaFileDownload /> Export
          </button>
        </div>
      </div>

      {/* KPI GRID */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '20px', marginBottom: '30px' }}>
        <KPICard title="Total Spend" value={metrics.totalSpend.toFixed(2)} prefix="₹" icon={<FaRupeeSign />} color={THEME.primary} />
        <KPICard title="Delivery Rate" value={metrics.deliveryRate + '%'} icon={<FaCheckDouble />} color={THEME.accent} trend={0} />
        <KPICard title="Read Rate" value={metrics.readRate + '%'} icon={<FaEye />} color={THEME.warning} trend={metrics.readRate - 40} />
        <KPICard title="Est. ROI" value={metrics.roi + '%'} icon={<FaPercentage />} color={metrics.roi > 0 ? THEME.success : THEME.danger} trend={metrics.roi} />
      </div>

      {/* CHARTS ROW */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: '20px', marginBottom: '30px' }}>
        
        {/* Trend Chart */}
        <div style={{ background: 'white', padding: '25px', borderRadius: '20px', border: `1px solid ${THEME.border}` }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '700', color: THEME.textMain }}>Spend vs Engagement</h3>
          <div style={{ height: '300px', width: '100%' }}>
            <ResponsiveContainer>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={THEME.accent} stopOpacity={0.3}/><stop offset="95%" stopColor={THEME.accent} stopOpacity={0}/></linearGradient>
                  <linearGradient id="colorRead" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={THEME.success} stopOpacity={0.3}/><stop offset="95%" stopColor={THEME.success} stopOpacity={0}/></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={THEME.border} />
                <XAxis dataKey="date" tick={{fontSize: 11}} axisLine={false} tickLine={false} />
                <YAxis tick={{fontSize: 11}} axisLine={false} tickLine={false} />
                <RechartsTooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }} />
                <Area type="monotone" dataKey="Spend" stroke={THEME.accent} fillOpacity={1} fill="url(#colorSpend)" strokeWidth={3} />
                <Area type="monotone" dataKey="Read" stroke={THEME.success} fillOpacity={1} fill="url(#colorRead)" strokeWidth={3} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Funnel Chart */}
        <div style={{ background: 'white', padding: '25px', borderRadius: '20px', border: `1px solid ${THEME.border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: '700', color: THEME.textMain, width: '100%' }}>Delivery Funnel</h3>
          <div style={{ height: '250px', width: '100%' }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={funnelData} innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                  <Cell fill={THEME.primary} />
                  <Cell fill={THEME.accent} />
                  <Cell fill={THEME.success} />
                </Pie>
                <RechartsTooltip />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* DETAILED REPORT TABLE */}
      <div style={{ background: 'white', borderRadius: '20px', border: `1px solid ${THEME.border}`, overflow: 'hidden' }}>
        <div style={{ padding: '20px', borderBottom: `1px solid ${THEME.border}` }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>Detailed Campaign Performance</h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8fafc', borderBottom: `1px solid ${THEME.border}` }}>
              <tr>
                {['Campaign', 'Date', 'Sent', 'Read', 'Cost (₹)', 'Performance'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '15px 20px', fontSize: '12px', fontWeight: '700', color: THEME.textSub, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.map(camp => {
                const cost = (camp.sent || 0) * COST_PER_MSG;
                const openRate = camp.sent ? Math.round((camp.read / camp.sent) * 100) : 0;
                return (
                  <tr key={camp.id} style={{ borderBottom: `1px solid ${THEME.border}` }}>
                    <td style={{ padding: '15px 20px', fontWeight: '600', color: THEME.textMain }}>{camp.name}</td>
                    <td style={{ padding: '15px 20px', color: THEME.textSub, fontSize: '13px' }}>
                      {camp.createdAt?.toDate ? camp.createdAt.toDate().toLocaleDateString() : '-'}
                    </td>
                    <td style={{ padding: '15px 20px', fontWeight: 'bold' }}>{camp.sent}</td>
                    <td style={{ padding: '15px 20px' }}>{camp.read}</td>
                    <td style={{ padding: '15px 20px', color: THEME.textMain }}>₹{cost.toFixed(2)}</td>
                    <td style={{ padding: '15px 20px' }}>
                      <span style={{ 
                        fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '20px',
                        background: openRate > 50 ? `${THEME.success}20` : openRate > 20 ? `${THEME.warning}20` : `${THEME.danger}20`,
                        color: openRate > 50 ? THEME.success : openRate > 20 ? THEME.warning : THEME.danger
                      }}>
                        {openRate}% Open Rate
                      </span>
                    </td>
                  </tr>
                );
              })}
              {filteredData.length === 0 && (
                 <tr><td colSpan="6" style={{ padding: '30px', textAlign: 'center', color: THEME.textSub }}>No data found for this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}

export default Analytics;